import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import api from '../services/api';
import { SERVICE_LABEL_TO_SLUG } from '../data/serviceLandingContent';
import { STATIC_SERVICES_CATALOG, getServicePath } from '../data/staticServicesCatalog';
import {
  SITE_ORIGIN,
  SITE_LOGO_URL,
  SITE_LOGO_PATH,
  OG_SHARE_IMAGE_URL,
  OG_SHARE_IMAGE_WIDTH,
  OG_SHARE_IMAGE_HEIGHT,
  SITE_BRAND_NAME,
  SITE_BRAND_LEGACY,
  SITE_BRAND_LEGACY_UPPER
} from '../constants/siteBranding';
import { buildServiceCardImgSrc } from '../utils/resolveMediaUrl';
import './ServicesPage.css';

const SERVICE_LIST_LABELS = [
  'House Keeping Sweeper', 'Office Boy, Peon', 'Receptionist', 'Helper', 'Labour',
  'Manpower Supply', 'Commercial Building Cleaning Staff', 'Recency Cleaning Staff',
  'Corporate Cleaning Staff', 'Hotel Cleaning Staff', 'Hospital Cleaning Staff',
  'Restaurant Cleaning Staff', 'Cafe Cleaning Staff', 'Malls Cleaning Staff',
  'Showroom Cleaning Staff', 'Shop Cleaning Staff', 'Office Cleaning Staff',
  'Pest Control Services', 'Society Cleaning Staff', 'Valet Parking Service',
  'Driver Service Agent', 'Deep Cleaning Service', 'Special Event Cleaning Works',
  'Carpet Cleaning Service', 'House Cleaning'
];

const SITE_NAME = SITE_BRAND_NAME;

const SERVICES_META_TITLE = `Housekeeping & Facility Services Ahmedabad | ${SITE_NAME}`;
const SERVICES_META_DESCRIPTION = `Housekeeping, valet parking, manpower and facility management from ${SITE_NAME} in Ahmedabad — SG Highway, Satellite, Bodakdev, Thaltej, Bopal, Vastrapur, Narol and across Gujarat. Formerly ${SITE_BRAND_LEGACY}. Call +91 92654 72938.`;

// Generate slug matching backend logic (avoids double hyphens from "Carpet & Sofa")
function slugFromTitle(title) {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// SEO keywords: valet parking, facility management, areas, events, cleaning (250+ terms)
const SEO_KEYWORDS = [
  'valet parking service in ahmedabad',
  'valet parking services ahmedabad',
  'valet parking services near me',
  'valet parking ahmedabad',
  'valet parking for wedding ahmedabad',
  'valet parking for events ahmedabad',
  'valet parking for corporate events',
  'valet parking at mall ahmedabad',
  'valet parking hotel ahmedabad',
  'valet parking hospital ahmedabad',
  'professional valet parking ahmedabad',
  'event valet parking ahmedabad',
  'wedding valet parking service',
  'valet parking cost ahmedabad',
  'valet parking thaltej',
  'valet parking bodakdev',
  'valet parking vastrapur',
  'valet parking satellite ahmedabad',
  'valet parking prahladnagar',
  'valet parking s.g highway',
  'valet parking iskcon ahmedabad',
  'valet parking near me',
  'facility management ahmedabad',
  'facility management services ahmedabad',
  'facility management company ahmedabad',
  'integrated facility management',
  'commercial facility management',
  'office facility management ahmedabad',
  'house keeping ahmedabad',
  'housekeeping services ahmedabad',
  'housekeeping company ahmedabad',
  'commercial housekeeping',
  'residential housekeeping ahmedabad',
  'office housekeeping',
  'cleaning services ahmedabad',
  'commercial cleaning ahmedabad',
  'deep cleaning ahmedabad',
  'office cleaning ahmedabad',
  'residential cleaning ahmedabad',
  'carpet cleaning ahmedabad',
  'sofa cleaning ahmedabad',
  'floor cleaning ahmedabad',
  'window cleaning ahmedabad',
  'disinfection services ahmedabad',
  'sanitization services ahmedabad',
  'laundry services ahmedabad',
  'industrial laundry ahmedabad',
  'janitorial services ahmedabad',
  'janitorial staff ahmedabad',
  'cleaning staff ahmedabad',
  'housekeeping staff ahmedabad',
  'security and facility management',
  'pest control ahmedabad',
  'gardening services ahmedabad',
  'building maintenance ahmedabad',
  'office maintenance ahmedabad',
  'event management ahmedabad',
  'staffing services ahmedabad',
  'trained cleaning staff',
  'commercial cleaning company',
  'best valet parking ahmedabad',
  'top facility management ahmedabad',
  'trusted housekeeping ahmedabad',
  'professional cleaning ahmedabad',
  'corporate cleaning services',
  'hospital cleaning services',
  'school cleaning services',
  'mall cleaning services',
  'restaurant cleaning',
  'cafe cleaning ahmedabad',
  'apartment cleaning ahmedabad',
  'post construction cleaning',
  'move in move out cleaning',
  'daily cleaning services',
  'weekly cleaning services',
  'monthly deep cleaning',
  'gujarat facility management',
  'ahmedabad gujarat cleaning',
  'thaltej shilaj road services',
  'ravija plaza ahmedabad',
  'bodakdev ahmedabad services',
  'vastrapur lake area',
  'satellite area ahmedabad',
  'sarkhej ahmedabad',
  'naroda ahmedabad',
  'narol ahmedabad',
  'odhav ahmedabad',
  'nikol ahmedabad',
  'maninagar ahmedabad',
  'gota ahmedabad',
  'chandkheda ahmedabad',
  'motera ahmedabad',
  'sabarmati ahmedabad',
  'navrangpura ahmedabad',
  'cg road ahmedabad',
  'sg highway ahmedabad',
  'driveway parking',
  'event parking management',
  'wedding parking service',
  'conference parking',
  'exhibition valet',
  'hotel valet service',
  'restaurant valet',
  'hospital parking management',
  'office building parking',
  'residential society parking',
  'mall parking management',
  'Khodal Housekeeping Agency',
  SITE_BRAND_LEGACY_UPPER,
  'khodal valet parking',
  'khodal ahmedabad',
  'valet parking meaning',
  'valet parking price',
  'valet parking charges',
  'car parking service ahmedabad',
  'vehicle parking management',
  'parking attendant service',
  'valet service near thaltej',
  'valet service near vastrapur',
  'house keeping agency ahmedabad',
  'cleaning agency ahmedabad',
  'facility management agency',
  'contract housekeeping',
  'outsourced cleaning services',
  'outsourced facility management',
  'amc cleaning contract',
  'annual maintenance contract cleaning',
  'building cleaning contract',
  'office cleaning contract ahmedabad',
  'deep cleaning price ahmedabad',
  'housekeeping charges ahmedabad',
  'cleaning service cost ahmedabad',
  'janitorial contract',
  'staffing solutions ahmedabad',
  'temporary staff ahmedabad',
  'cleaning labour ahmedabad',
  'sweeper services',
  'mopping and cleaning',
  'toilet cleaning services',
  'washroom cleaning',
  'kitchen deep cleaning',
  'ac cleaning ahmedabad',
  'duct cleaning',
  'water tank cleaning',
  'terrace cleaning',
  'garden maintenance ahmedabad',
  'lawn care ahmedabad',
  'waste management ahmedabad',
  'segregation services',
  'receptionist services ahmedabad',
  'front desk staff',
  'peon services ahmedabad',
  'office boy services',
  'helper services',
  'event staff ahmedabad',
  'wedding staff ahmedabad',
  'banquet staff',
  'waiting staff ahmedabad',
  'chef on contract',
  'cook services ahmedabad',
  'driver services ahmedabad',
  'valet parking for marriage',
  'marriage valet service',
  'party valet parking',
  'birthday party valet',
  'corporate event valet',
  'seminar parking',
  'conference valet ahmedabad',
  'exhibition parking service',
  'concert valet',
  'sports event parking',
  'stadium parking management',
  'temple valet parking',
  'religious event parking',
  'festival parking ahmedabad',
  'dandiya parking',
  'navratri valet service',
  'best cleaning service ahmedabad',
  'top housekeeping ahmedabad',
  'affordable valet parking',
  'cheap cleaning services ahmedabad',
  'professional valet',
  'trained valet staff',
  'reliable housekeeping',
  '24 hour cleaning',
  'emergency cleaning ahmedabad',
  'same day cleaning',
  'quick cleaning service',
  'green cleaning ahmedabad',
  'eco friendly cleaning',
  'chemical free cleaning',
  'steam cleaning ahmedabad',
  'pressure washing',
  'fabric cleaning',
  'upholstery cleaning',
  'curtain cleaning',
  'mattress cleaning',
  'car interior cleaning',
  'industrial cleaning ahmedabad',
  'factory cleaning',
  'warehouse cleaning',
  'godown cleaning',
  'showroom cleaning',
  'retail store cleaning',
  'bank cleaning services',
  'atm cleaning',
  'pharmacy cleaning',
  'clinic cleaning',
  'dental clinic cleaning',
  'gym cleaning',
  'salon cleaning',
  'spa cleaning',
  'swimming pool cleaning',
  'society cleaning',
  'apartment society housekeeping',
  'society maintenance',
  'common area cleaning',
  'lift cleaning',
  'staircase cleaning',
  'parking area cleaning',
  'compound cleaning',
  'entrance cleaning',
  'reception area cleaning',
  'cubicle cleaning',
  'workstation cleaning',
  'desk cleaning',
  'whiteboard cleaning',
  'glass cleaning',
  'metal polishing',
  'marble polishing ahmedabad',
  'wood polishing',
  'floor polishing',
  'tiles cleaning',
  'granite cleaning',
  'vitrified floor cleaning',
  'epoxy floor cleaning',
  'factory floor cleaning',
  'warehouse floor',
  'office interior cleaning',
  'post renovation cleaning',
  'pre opening cleaning',
  'handover cleaning',
  'end of tenancy cleaning',
  'bond cleaning',
  'spring cleaning',
  'festival cleaning',
  'diwali cleaning',
  'deep clean before festival',
  'commercial laundry ahmedabad',
  'hotel laundry',
  'hospital laundry',
  'uniform washing',
  'linen washing',
  'towel washing',
  'dry cleaning ahmedabad',
  'ironing services',
  'laundry pickup delivery',
  'wash and fold',
  'institutional laundry',
  'school uniform washing',
  'restaurant napkin washing',
  'cleaning services gujarat',
  'house keeping gujarat',
  'valet parking gujarat',
  'facility management gujarat',
  'vadodara cleaning',
  'surat housekeeping',
  'rajkot facility management',
  'gandhinagar valet parking',
  'call 9265472938',
  'contact khodal',
  'free quote ahmedabad',
  'get quote cleaning',
  'book valet parking',
  'hire housekeeping ahmedabad',
  'best facility management company',
  'trusted valet parking ahmedabad',
  'valet parking service in thaltej',
  'valet parking service in bodakdev',
  'valet parking service in vastrapur',
  'valet parking service in satellite',
  'valet parking service in prahladnagar',
  'valet parking service in sarkhej',
  'valet parking service in navrangpura',
  'valet parking service in gota',
  'valet parking service in chandkheda',
  'valet parking service in motera',
  'valet parking service in maninagar',
  'cleaning service thaltej',
  'cleaning service bodakdev',
  'cleaning service vastrapur',
  'housekeeping thaltej',
  'housekeeping bodakdev',
  'facility management thaltej',
  'facility management vastrapur',
  'office cleaning thaltej',
  'office cleaning bodakdev',
  'commercial cleaning vastrapur',
  'deep cleaning satellite',
  'janitorial services thaltej',
  'laundry service ahmedabad',
  'valet parking near ravija plaza',
  'valet parking near iskcon',
  'valet parking near alpha mall',
  'valet parking near palladium',
  'valet parking for engagement',
  'valet parking for reception',
  'valet parking for sangeet',
  'valet parking for corporate',
  'valet parking for product launch',
  'valet parking for award function',
  'valet parking for concert',
  'valet parking for exhibition',
  'valet parking for trade show',
  'valet parking for conference',
  'valet parking for seminar',
  'valet parking for workshop',
  'valet parking for garba',
  'valet parking for dandiya night',
  'marriage hall valet',
  'banquet valet parking',
  'resort valet parking',
  'farmhouse valet',
  'club valet parking',
  'gym parking management',
  'school parking management',
  'college valet service',
  'university parking',
  'mall parking service',
  'multiplex valet',
  'hospital valet parking',
  'clinic parking',
  'corporate park valet',
  'it park parking',
  'sez parking management',
  'business park valet',
  'commercial complex parking',
  'residential complex valet',
  'society valet parking',
  'apartment valet service',
  'bungalow valet parking',
  'villa valet service',
  'guest parking management',
  'visitor parking',
  'vip valet service',
  'premium valet parking',
  'luxury valet service',
  'budget valet parking',
  'economical cleaning ahmedabad',
  'quality housekeeping',
  'certified cleaning staff',
  'background verified staff',
  'insured cleaning company',
  'licensed facility management',
  'experienced valet team',
  'trained parking attendants',
  'uniform valet staff',
  'polite valet service',
  'quick valet service',
  'safe valet parking',
  'secure parking management',
  'car key handling',
  'vehicle safety valet',
  'parking lot management',
  'parking space management',
  'traffic management events',
  'crowd management parking',
  'entry exit management',
  'valet parking setup',
  'valet stand',
  'valet desk',
  'valet counter',
  'temporary valet service',
  'one day valet',
  'weekly valet contract',
  'monthly valet contract',
  'annual valet contract',
  'event based valet',
  'regular valet service',
  'on demand valet',
  'valet parking booking',
  'online valet booking',
  'valet parking quote',
  'valet parking rates',
  'valet parking packages',
  'valet parking plans',
  'custom valet solution',
  'tailored facility management',
  'bespoke cleaning plan',
  'flexible housekeeping',
  'scalable cleaning services',
  'multi site facility management',
  'pan ahmedabad service',
  'all area ahmedabad',
  'west ahmedabad cleaning',
  'east ahmedabad housekeeping',
  'north ahmedabad valet',
  'south ahmedabad facility',
  'new ahmedabad services',
  'old ahmedabad cleaning',
  'ahmedabad city services',
  'ahmedabad outskirts',
  'ahmedabad rural',
  'ahmedabad urban',
  'metro ahmedabad',
  'ahmedabad smart city',
  'gift city facility',
  'gift city cleaning',
  'dholera facility management',
  'sanand cleaning',
  'sanand valet',
  'gandhinagar cleaning',
  'gandhinagar facility',
  'nirma university area',
  'iim ahmedabad area',
  'cept area services',
  'law garden area',
  'c g road services',
  's g road cleaning',
  'sarkhej gandhinagar highway',
  '150 feet ring road',
  '132 feet ring road',
  'science city area',
  'kankaria area',
  'riverfront valet',
  'sabarmati riverfront',
  'reliable cleaning ahmedabad',
  'punctual housekeeping',
  'quality assured cleaning',
  'satisfaction guaranteed',
  'customer first cleaning',
  'transparent pricing cleaning',
  'no hidden charges',
  'free site visit',
  'instant quote',
  'same day service',
  'quick response',
  '24 7 support',
  'emergency valet',
  'last minute valet',
  'weekend valet service',
  'holiday valet',
  'festival cleaning',
  'special event valet',
  'grand opening valet',
  'inauguration parking',
  'foundation day event',
  'annual day valet',
  'sports day parking',
  'cultural event valet',
  'college fest valet',
  'corporate annual day',
  'team outing support',
  'offsite event parking',
  'indoor event valet',
  'outdoor event valet',
  'lawn event parking',
  'terrace event valet',
  'rooftop event parking',
  'pool side event',
  'garden party valet',
  'farmhouse event',
  'destination wedding valet',
  'destination wedding cleaning',
  'wedding venue cleaning',
  'pre wedding cleaning',
  'post wedding cleaning',
  'bride side valet',
  'groom side valet',
  'both side valet',
  'reception valet',
  'engagement valet parking',
  'mehndi valet',
  'sangeet parking',
  'haldi ceremony',
  'wedding cleaning services',
  'marriage hall cleaning',
  'banquet cleaning',
  'party hall cleaning',
  'conference room cleaning',
  'meeting room cleaning',
  'boardroom cleaning',
  'training room cleaning',
  'auditorium cleaning',
  'hall cleaning ahmedabad',
  'stadium cleaning',
  'arena cleaning',
  'exhibition hall',
  'trade fair cleaning',
  'pavilion cleaning',
  'booth cleaning',
  'kiosk cleaning',
  'retail cleaning',
  'store opening cleaning',
  'shop cleaning',
  'showroom opening',
  'new office cleaning',
  'renovation cleaning',
  'construction cleanup',
  'post paint cleaning',
  'dust removal',
  'debris removal',
  'pre opening deep clean',
  'handover cleaning',
  'takeover cleaning',
  'move in cleaning',
  'move out cleaning',
  'end of lease cleaning',
  'tenant cleaning',
  'landlord cleaning',
  'property cleaning',
  'real estate cleaning',
  'builder cleaning',
  'developer cleaning',
  'project cleaning',
  'site cleaning',
  'camp cleaning',
  'campus cleaning',
  'institute cleaning',
  'training center cleaning',
  'coaching class cleaning',
  'library cleaning',
  'museum cleaning',
  'gallery cleaning',
  'theater cleaning',
  'stadium cleaning services',
  'sports complex cleaning',
  'club house cleaning',
  'community center cleaning',
  'religious place cleaning',
  'temple cleaning',
  'mosque cleaning',
  'church cleaning',
  'gurudwara cleaning',
  'ashram cleaning',
  'trust cleaning',
  'ngo cleaning',
  'government office cleaning',
  'municipal cleaning',
  'panchayat cleaning',
  'gram panchayat',
  'village cleaning',
  'taluka cleaning',
  'district level services',
  'state level facility',
  'multi city facility management',
  'pan india facility',
  'national level cleaning',
  'international standard cleaning',
  'iso cleaning company',
  'certified facility management',
  'leed cleaning',
  'green building cleaning',
  'sustainable facility',
  'eco facility management',
  'water saving cleaning',
  'energy efficient facility',
  'recycling waste management',
  'waste recycling',
  'dry waste',
  'wet waste',
  'biodegradable',
  'e waste management',
  'hazardous waste',
  'medical waste',
  'industrial waste',
  'office waste',
  'kitchen waste',
  'garden waste',
  'cleaning waste disposal',
  'sewage management',
  'drain cleaning',
  'septic tank',
  'soak pit',
  'parking shade',
  'covered parking',
  'open parking',
  'underground parking',
  'basement parking',
  'multi level parking',
  'roof parking',
  'terrace parking',
  'guest parking',
  'visitor management',
  'security plus valet',
  'integrated services',
  'one stop facility',
  'single vendor facility',
  'total facility management',
  'tfm ahmedabad',
  'ifm ahmedabad',
  'soft services',
  'hard services',
  'mep maintenance',
  'electrical maintenance',
  'plumbing maintenance',
  'hvac maintenance',
  'ac maintenance',
  'lift maintenance',
  'fire safety',
  'fire fighting',
  'pest control service',
  'fumigation',
  'rodent control',
  'insect control',
  'sanitization service',
  'disinfection service',
  'covid sanitization',
  'post covid cleaning',
  'hygiene audit',
  'cleaning audit',
  'facility audit',
  'inspection service',
  'quality check cleaning',
  'sla based facility',
  'kpi based cleaning',
  'performance based contract',
  'output based cleaning',
  'result oriented',
  'khodal facility',
  '9265472938',
  'call now ahmedabad',
  'house keeping sweeper ahmedabad',
  'house keeping sweeper',
  'sweeper services ahmedabad',
  'office boy ahmedabad',
  'office boy peon',
  'peon services ahmedabad',
  'office peon',
  'receptionist services ahmedabad',
  'receptionist ahmedabad',
  'front desk receptionist',
  'helper services ahmedabad',
  'helper ahmedabad',
  'labour supply ahmedabad',
  'labour ahmedabad',
  'manpower supply ahmedabad',
  'manpower supply company ahmedabad',
  'contract labour ahmedabad',
  'commercial building cleaning staff',
  'commercial building cleaning ahmedabad',
  'building cleaning staff',
  'recency cleaning staff',
  'regular cleaning staff',
  'corporate cleaning staff ahmedabad',
  'corporate cleaning ahmedabad',
  'hotel cleaning staff ahmedabad',
  'hotel cleaning services',
  'hospital cleaning staff ahmedabad',
  'hospital cleaning services',
  'restaurant cleaning staff ahmedabad',
  'restaurant cleaning services',
  'cafe cleaning staff ahmedabad',
  'cafe cleaning services',
  'malls cleaning staff ahmedabad',
  'mall cleaning services',
  'showroom cleaning staff ahmedabad',
  'showroom cleaning',
  'shop cleaning staff ahmedabad',
  'shop cleaning services',
  'office cleaning staff ahmedabad',
  'office cleaning staff',
  'pest control services ahmedabad',
  'pest control ahmedabad',
  'society cleaning staff ahmedabad',
  'society cleaning services',
  'apartment society cleaning',
  'valet parking service ahmedabad',
  'valet parking service',
  'driver service agent ahmedabad',
  'driver services ahmedabad',
  'driver agent',
  'deep cleaning service ahmedabad',
  'deep cleaning service',
  'special event cleaning ahmedabad',
  'special event cleaning works',
  'event cleaning staff',
  'carpet cleaning service ahmedabad',
  'carpet cleaning service',
  'house cleaning ahmedabad',
  'house cleaning',
  'residential house cleaning',
  'house keeping sweeper near me',
  'office boy peon ahmedabad',
  'receptionist near me',
  'helper labour ahmedabad',
  'manpower supply near me',
  'commercial cleaning staff',
  'corporate office cleaning',
  'hotel housekeeping staff',
  'hospital janitorial staff',
  'restaurant kitchen cleaning',
  'cafe cleaning',
  'mall housekeeping',
  'showroom cleaning staff',
  'shop cleaning',
  'office boy and peon',
  'pest control company ahmedabad',
  'society housekeeping',
  'valet parking staff',
  'driver on contract',
  'deep cleaning company',
  'event cleaning company',
  'carpet and sofa cleaning',
  'home cleaning ahmedabad'
];

function ServicesPage() {
  const [services, setServices] = useState(() => STATIC_SERVICES_CATALOG);

  useEffect(() => {
    api
      .get('/services')
      .then((res) => {
        if (res.data?.length) setServices(res.data);
      })
      .catch(() => {});
  }, []);

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) acc[service.category] = [];
    acc[service.category].push(service);
    return acc;
  }, {});

  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/services`
    : `${SITE_ORIGIN}/services`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'LocalBusiness',
        '@id': `${canonicalUrl}#organization`,
        name: SITE_NAME,
        image: [OG_SHARE_IMAGE_URL, SITE_LOGO_URL],
        logo: SITE_LOGO_URL,
        description: `Housekeeping, valet parking, manpower and facility management in Ahmedabad and Gujarat — SG Highway, Satellite, Bodakdev, Thaltej, Bopal, Narol. ${SITE_NAME}.`,
        url: typeof window !== 'undefined' ? window.location.origin : SITE_ORIGIN,
        telephone: '+919265472938',
        email: 'khodalhousekeeping2383@gmail.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Office No. 210, Ravija Plaza, Thaltej Shilaj Road',
          addressLocality: 'Ahmedabad',
          addressRegion: 'Gujarat',
          postalCode: '380059',
          addressCountry: 'IN'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 23.0225,
          longitude: 72.5714
        },
        areaServed: [
          { '@type': 'City', name: 'Ahmedabad', containedInPlace: { '@type': 'State', name: 'Gujarat', addressCountry: 'IN' } },
          { '@type': 'AdministrativeArea', name: 'Gujarat' }
        ],
        hasMap: 'https://maps.app.goo.gl/UtJBLHx2XrZozVJNA',
        knowsAbout: [
          'House Keeping Sweeper',
          'Office Boy Peon',
          'Receptionist',
          'Helper',
          'Labour',
          'Manpower Supply',
          'Commercial Building Cleaning Staff',
          'Recency Cleaning Staff',
          'Corporate Cleaning Staff',
          'Hotel Cleaning Staff',
          'Hospital Cleaning Staff',
          'Restaurant Cleaning Staff',
          'Cafe Cleaning Staff',
          'Malls Cleaning Staff',
          'Showroom Cleaning Staff',
          'Shop Cleaning Staff',
          'Office Cleaning Staff',
          'Pest Control Services',
          'Society Cleaning Staff',
          'Valet Parking Service',
          'Driver Service Agent',
          'Deep Cleaning Service',
          'Special Event Cleaning Works',
          'Carpet Cleaning Service',
          'House Cleaning',
          'Facility management Ahmedabad',
          'Valet parking service in Ahmedabad'
        ]
      },
      {
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#services-list`,
        itemListElement: services.map((s, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Service',
            name: s.title,
            description: s.description || `${s.title} in Ahmedabad - Professional service by ${SITE_NAME}`,
            provider: { '@id': `${canonicalUrl}#organization` }
          }
        }))
      }
    ]
  };

  return (
    <div className="services-page">
      <Helmet>
        <title>{SERVICES_META_TITLE}</title>
        <meta name="description" content={SERVICES_META_DESCRIPTION} />
        <meta name="keywords" content={SEO_KEYWORDS.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={SERVICES_META_TITLE} />
        <meta property="og:description" content={SERVICES_META_DESCRIPTION} />
        <meta property="og:image" content={OG_SHARE_IMAGE_URL} />
        <meta property="og:image:width" content={String(OG_SHARE_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(OG_SHARE_IMAGE_HEIGHT)} />
        <meta property="og:image:alt" content={`${SITE_NAME} — housekeeping, valet and facility services Ahmedabad`} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content={SERVICES_META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_SHARE_IMAGE_URL} />
        <meta name="twitter:image:alt" content={`${SITE_NAME} - Valet parking & facility management Ahmedabad`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="services-page-main">
        <article className="services-article">
          <header className="services-hero">
            <div className="container">
              <div className="services-hero-logo-wrap" aria-hidden="true">
                <img
                  src={`${process.env.PUBLIC_URL || ''}${SITE_LOGO_PATH}`}
                  alt=""
                  className="services-hero-logo"
                  width={240}
                  height={80}
                  decoding="async"
                />
              </div>
              <h1 className="services-h1">
                Valet Parking, Facility Management & Cleaning Services in Ahmedabad
              </h1>
              <p className="services-intro">
                {SITE_NAME} offers <Link to="/service/valet-parking-ahmedabad" className="services-intro-link">valet parking service in Ahmedabad</Link>, facility management, house keeping, commercial cleaning, laundry, and janitorial services. Trusted by offices, events, residential complexes, and businesses across Ahmedabad and Gujarat.
              </p>
            </div>
          </header>

          <section className="services-content container" aria-labelledby="our-services">
            <h2 id="our-services" className="visually-hidden">Our Services</h2>

            <>
              {Object.keys(groupedServices).map((category) => (
                <section
                  key={category}
                  className="service-category-block"
                  aria-labelledby={`category-${category.replace(/\s+/g, '-')}`}
                >
                  <h3 id={`category-${category.replace(/\s+/g, '-')}`} className="category-heading">
                    {category} in Ahmedabad
                  </h3>
                  <ul className="services-grid" itemScope itemType="https://schema.org/ItemList">
                    {groupedServices[category].map((service, idx) => (
                      <li
                        key={service.id || service._id}
                        className="service-card"
                        itemProp="itemListElement"
                        itemScope
                        itemType="https://schema.org/ListItem"
                      >
                        <meta itemProp="position" content={idx + 1} />
                        <Link
                          to={getServicePath(service.slug || slugFromTitle(service.title))}
                          className="service-card-link"
                        >
                          <article itemProp="item" itemScope itemType="https://schema.org/Service">
                            <div className="service-card-image-wrap">
                              <img
                                src={buildServiceCardImgSrc(service.image_url)}
                                alt={`${service.title} — ${SITE_BRAND_NAME}, Ahmedabad`}
                                width="400"
                                height="240"
                                loading="lazy"
                                itemProp="image"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  if (!e.currentTarget.src.includes('KHP_LOGO')) {
                                    e.currentTarget.src = buildServiceCardImgSrc(null);
                                  }
                                }}
                              />
                            </div>
                            <div className="service-card-content">
                              <h4 itemProp="name">{service.title}</h4>
                              {service.description && (
                                <p itemProp="description" className="service-card-desc">
                                  {service.description}
                                </p>
                              )}
                              <p className="service-location-badge">
                                Available in Ahmedabad & Gujarat
                              </p>
                            </div>
                          </article>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </>
          </section>

          <section className="services-list-section container" aria-labelledby="all-services-heading">
            <h2 id="all-services-heading" className="services-list-heading">Services We Offer in Ahmedabad & Gujarat</h2>
            <p className="services-list-intro">
              {SITE_NAME} provides House Keeping Sweeper, Office Boy, Peon, Receptionist, Helper, Labour, Manpower Supply, Commercial Building Cleaning Staff, Recency Cleaning Staff, Corporate Cleaning Staff, Hotel Cleaning Staff, Hospital Cleaning Staff, Restaurant Cleaning Staff, Cafe Cleaning Staff, Malls Cleaning Staff, Showroom Cleaning Staff, Shop Cleaning Staff, Office Cleaning Staff, Pest Control Services, Society Cleaning Staff, Valet Parking Service, Driver Service Agent, Deep Cleaning Service, Special Event Cleaning Works, Carpet Cleaning Service, and House Cleaning in Ahmedabad and across Gujarat.
            </p>
            <ul className="services-keywords-list">
              {SERVICE_LIST_LABELS.map((label) => {
                const slug = SERVICE_LABEL_TO_SLUG[label];
                return (
                  <li key={slug}>
                    {slug ? <Link to={`/service/${slug}`}>{label}</Link> : label}
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="services-cta-section">
            <div className="container">
              <h2>Book Valet Parking & Facility Services in Ahmedabad</h2>
              <p>
                Need valet parking service, house keeping, commercial cleaning, or laundry in Ahmedabad? Contact us for a quote.
              </p>
              <a href="/inquiry" className="services-cta-btn">Get Free Quote</a>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}

export default ServicesPage;
